import React, { useEffect, useRef, useState } from 'react';
import { motion, useIsPresent } from 'framer-motion';
import logger from '../../utils/logger';
import { useAPI } from '../../contexts/APIContext';
import { fetchFirstEpisode, stopEpisode } from '../FunnelWaitVideo';
import { StarOutline, MotionLines, Chevron, PIP_POSTER } from './FunnelScreens';

/**
 * The three explainer slides — meet a friend, watch a story, read a book —
 * shown by both web2app funnels: /funnel-auth/intro walks every visitor
 * through them, /funnel-auth only the ones who tap "See how Pixley works".
 *
 * Presentation only, like FunnelScreens: each page owns its step state and
 * analytics, renders <ExplainerSlide> inside its own card, and renders
 * <FunnelExplainerStyles /> once.
 */

// One button per slide, in order; only the last one ("Create their character")
// moves on to naming. The keys double as the funnels' step names.
export const EXPLAINER_SLIDES = [
  {
    key: 'friend',
    media: 'cast',
    eyebrow: 'A character they can call their own',
    title: <>Start with a friend.<br /><span className="fs-accent">Make it theirs.</span></>,
    cta: 'Explore the stories',
    ctaGoesNext: true,
  },
  {
    key: 'episode',
    media: 'story',
    eyebrow: 'Watch a story',
    title: <>See their friend<br />in a <span className="fs-accent">cartoon</span>.</>,
    body: 'Create a story starring the character you choose, then watch it together.',
    cta: 'See the storybooks',
    ctaGoesNext: true,
  },
  {
    key: 'personal',
    media: 'book',
    eyebrow: 'Read a book',
    title: <>Read the adventure<br /><span className="fs-accent">together.</span></>,
    body: 'Turn illustrated pages at your own pace, with the character you choose at the center of the story.',
    cta: 'Create their character',
  },
];

export const EXPLAINER_STEPS = EXPLAINER_SLIDES.map((s) => s.key);
export const EXPLAINER_BY_KEY = Object.fromEntries(EXPLAINER_SLIDES.map((s) => [s.key, s]));

// The story player's cover, until they press play
const STORY_POSTER = '/assets/funnel/story-preview.webp';

// Slide one's own art — worth warming on the welcome screen when the explainer
// is where every visitor goes next
export const EXPLAINER_FIRST_ART = [
  '/assets/funnel/benny-cutout.webp',
  '/assets/funnel-friends/zippy-icon.webp',
  '/assets/funnel-friends/willow.webp',
];

// Art to warm while each slide is up, one screen ahead of where it's needed,
// so nothing pops in
export const EXPLAINER_PRELOAD_AHEAD = {
  friend: [STORY_POSTER],
  episode: ['/assets/funnel/storybook-spread.webp'],
  personal: [PIP_POSTER],
};

/* Slide one: Benny up front, Zippy and Willow on tilted cards either side */
const FriendCast = () => (
  <div className="fx-cast" aria-hidden="true">
    <StarOutline className="fs-star fx-cast-star-a" />
    <StarOutline className="fs-star fx-cast-star-b" />
    <StarOutline className="fs-star fx-cast-star-c" />
    <img className="fx-cast-hero" src="/assets/funnel/benny-cutout.webp" alt="" width="387" height="640" />
    <MotionLines className="fs-lines fx-cast-lines" />
    <motion.span
      className="fx-cast-card fx-cast-card-l"
      initial={{ opacity: 0, rotate: 0, y: 14 }}
      animate={{ opacity: 1, rotate: -11, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
    >
      <img src="/assets/funnel-friends/zippy-icon.webp" alt="" />
    </motion.span>
    <motion.span
      className="fx-cast-card fx-cast-card-r"
      initial={{ opacity: 0, rotate: 0, y: 14 }}
      animate={{ opacity: 1, rotate: 11, y: 0 }}
      transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
    >
      <img src="/assets/funnel-friends/willow.webp" alt="" />
    </motion.span>
  </div>
);

/* Slide two: a real Pixley Original — the same episode the wait screen
   offers — playing by itself, muted, as soon as the slide is up. The
   browser's own controls stay, to turn the sound on or scrub. It streams, so
   only what's watched is fetched (the file is hundreds of MB). Where autoplay
   is off (iOS Low Power Mode) the controls' own play button starts it.
   Leaving the slide tears it down the way the wait screen's player does, so
   it can't play on in Picture-in-Picture. If the episode can't be loaded, the
   cover art stands alone. */
const StoryPlayer = () => {
  const { apiClient } = useAPI();
  const [src, setSrc] = useState(null);
  const videoRef = useRef(null);
  // False as soon as the slide starts leaving — the card stays mounted through
  // its exit animation (and that animation stalls in a background tab)
  const isPresent = useIsPresent();

  useEffect(() => {
    if (!apiClient) return undefined;
    let cancelled = false;
    fetchFirstEpisode(apiClient)
      .then((episode) => {
        if (!cancelled && episode) setSrc(episode.file_url);
      })
      .catch((error) => logger.warn('Funnel explainer story unavailable:', error));
    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  useEffect(() => {
    if (!src) return undefined;
    const video = videoRef.current;
    return () => stopEpisode(video);
  }, [src]);

  // Stop the moment they move on, not when the outgoing card finally unmounts
  useEffect(() => {
    if (!isPresent) stopEpisode(videoRef.current);
  }, [isPresent]);

  return (
    <div className="fx-story">
      {src ? (
        <>
          <video
            ref={videoRef}
            className="fx-story-video"
            src={src}
            poster={STORY_POSTER}
            controls
            autoPlay
            muted
            playsInline
          />
        </>
      ) : (
        <img className="fx-story-video" src={STORY_POSTER} alt="Zippy the tiger setting off toward a castle" />
      )}
    </div>
  );
};

const PageArrow = ({ direction }) => (
  <span className={`fx-tablet-arrow fx-tablet-arrow-${direction}`} aria-hidden="true">
    <svg viewBox="0 0 24 24" width="18" height="18">
      <path
        d={direction === 'l' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'}
        fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  </span>
);

/* Slide three: a storybook spread on a tablet. The page arrows are part of
   the picture, not controls — there's one spread to show. */
const StorybookTablet = () => (
  <div className="fx-tablet">
    <img
      src="/assets/funnel/storybook-spread.webp"
      alt="A Pixley storybook page: Zippy followed the winding path."
      width="1200"
      height="900"
    />
    <PageArrow direction="l" />
    <PageArrow direction="r" />
  </div>
);

export const ExplainerSlide = ({ slide, onCreate, onNext, itemVariants }) => (
  <>
    <motion.div variants={itemVariants} className="fx-media">
      {slide.media === 'cast' && <FriendCast />}
      {slide.media === 'story' && <StoryPlayer />}
      {slide.media === 'book' && <StorybookTablet />}
    </motion.div>

    <motion.div variants={itemVariants} className="fs-eyebrow">{slide.eyebrow}</motion.div>
    <motion.h1
      variants={itemVariants}
      className={`fs-title fx-title${slide.body ? '' : ' fx-title-last'}`}
    >
      {slide.title}
    </motion.h1>
    {slide.body && (
      <motion.p variants={itemVariants} className="fs-sub fx-body">{slide.body}</motion.p>
    )}

    <motion.div variants={itemVariants}>
      <button className="fs-cta" onClick={slide.ctaGoesNext ? onNext : onCreate}>
        {slide.cta} <Chevron />
      </button>
    </motion.div>
  </>
);

// The art runs nearly to the card's edges; both funnels' cards share the same
// padding (26px, 18px on phones), which the negative margins undo.
export const FunnelExplainerStyles = () => (
  <style>{`
    .fx-media { margin: -14px -14px 26px; }
    /* Compound selectors, so these win over the .fs-* rules of equal weight
       whichever order the two sheets render in */
    .fs-title.fx-title { margin-bottom: 12px; }
    .fs-title.fx-title-last { margin-bottom: 24px; }
    .fs-sub.fx-body { color: #1F2B45; margin-bottom: 24px; }

    /* Slide one: Benny up front, Zippy and Willow on tilted cards either side */
    .fx-cast {
      position: relative;
      max-width: 400px;
      height: 300px;
      margin: 0 auto;
      background: radial-gradient(58% 52% at 50% 46%, #EAF1FE 0%, rgba(234,241,254,0) 100%);
    }
    .fx-cast-hero {
      position: absolute; left: 53%; bottom: 0;
      height: 94%; width: auto;
      transform: translateX(-50%);
      filter: drop-shadow(0 14px 20px rgba(22,38,66,0.12));
    }
    .fx-cast-card {
      position: absolute;
      width: 28%;
      aspect-ratio: 1;
      overflow: hidden;
      border: 5px solid #FFF;
      border-radius: 22px;
      box-shadow: 0 10px 24px rgba(22,38,66,0.14);
    }
    /* The source art is full-body; the cards want faces */
    .fx-cast-card img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .fx-cast-card-l { left: 2%; top: 34%; }
    .fx-cast-card-l img { transform: scale(1.55); transform-origin: 50% 36%; }
    .fx-cast-card-r { right: 2%; top: 47%; }
    .fx-cast-card-r img { transform: scale(2.1); transform-origin: 56% 30%; }
    .fx-cast-star-a { width: 38px; height: 38px; left: 12%; top: 14%; transform: rotate(-12deg); }
    .fx-cast-star-b { width: 34px; height: 34px; left: 17%; bottom: 3%; transform: rotate(8deg); }
    .fx-cast-star-c { width: 36px; height: 36px; right: 11%; top: 18%; transform: rotate(14deg); }
    .fx-cast-lines { width: 46px; height: 46px; left: 64%; top: 4%; }

    /* Slide two: a story to watch */
    .fx-story {
      position: relative;
      aspect-ratio: 1.39;
      max-height: 44vh;
      overflow: hidden;
      border-radius: 24px;
      background: #0B1433;
      box-shadow: 0 8px 22px rgba(40,62,128,0.12);
    }
    .fx-story-video {
      position: absolute; inset: 0;
      width: 100%; height: 100%;
      object-fit: cover;
      display: block;
    }
    /* Keep play/pause in the control bar, as in the design — Chrome drops it
       from narrow players otherwise */
    .fx-story-video::-webkit-media-controls-play-button { display: flex !important; }

    /* Slide three: the storybook on a tablet */
    .fx-tablet {
      position: relative;
      padding: 9px;
      border-radius: 24px;
      background: linear-gradient(145deg, #2B3243 0%, #10141D 100%);
      box-shadow: 0 16px 34px rgba(22,38,66,0.24), inset 0 0 0 1px rgba(255,255,255,0.10);
    }
    .fx-tablet img {
      display: block;
      width: 100%; height: auto;
      max-height: 40vh;
      object-fit: cover;
      border-radius: 15px;
    }
    .fx-tablet-arrow {
      position: absolute; top: 62%;
      width: 36px; height: 36px;
      display: flex; align-items: center; justify-content: center;
      border-radius: 50%;
      background: #FFF; color: #296FFB;
      box-shadow: 0 4px 12px rgba(22,38,66,0.22);
      transform: translateY(-50%);
      pointer-events: none;
    }
    .fx-tablet-arrow-l { left: 20px; }
    .fx-tablet-arrow-r { right: 20px; }

    @media (max-width: 480px) {
      .fx-media { margin: -12px -8px 22px; }
      .fx-cast { height: 272px; }
      .fx-story { border-radius: 22px; }
      .fx-tablet { padding: 8px; border-radius: 22px; }
      .fx-tablet img { border-radius: 14px; }
      .fx-tablet-arrow { width: 32px; height: 32px; }
      .fx-tablet-arrow-l { left: 16px; }
      .fx-tablet-arrow-r { right: 16px; }
    }
    @media (max-width: 374px) {
      .fx-cast { height: 236px; }
      .fx-cast-star-a, .fx-cast-star-b, .fx-cast-star-c { width: 28px; height: 28px; }
    }
    @media (max-height: 700px) {
      .fx-media { margin-bottom: 16px; }
      .fx-cast { height: 220px; }
      .fx-story { max-height: 36vh; }
      .fx-tablet img { max-height: 32vh; }
    }
  `}</style>
);
